import {ref, type Ref, watch} from 'vue'
import {get, set} from 'idb-keyval'
import cloneDeep from "lodash/cloneDeep";


/**
 * - `mergeDefaults`: Adds missing fields from initial value (like in `useLocalStorage` from vueuse). See https://vueuse.org/core/useStorage/#merge-defaults
 * - `migrateFromLocalStorage`: Only migrates if data exists in `localStorage` and not in IndexedDB.
 * - `clearLocalStorageAfterMigration`: Deletes the localStorage key after successful migration.
 */
type Options = {
    mergeDefaults?: boolean
    migrateFromLocalStorage?: boolean
    clearLocalStorageAfterMigration?: boolean
}


/**
 * Meant to be an alternative to useLocalStorage from vueuse/core.
 *
 * Writes to IndexedDB instead, which does not have the same low storage space limit.
 * Writes live on changes, no need to call any set method manually.
 *
 * @param key
 * @param initialValue
 * @param options
 */
export function useIndexedDBStorage<T>(
    key: string,
    initialValue: T,
    options: Options = {}
): Ref<T> {
    const data = ref<T>(initialValue) as Ref<T>

    const tryMigrateFromLocalStorage = (): T | undefined => {
        if (!options.migrateFromLocalStorage) return undefined
        try {
            const raw = localStorage.getItem(key)
            if (raw) {
                const parsed = JSON.parse(raw) as T
                if (options.clearLocalStorageAfterMigration) {
                    localStorage.removeItem(key)
                }
                return parsed
            }
        } catch (e) {
            console.error(`Failed to migrate ${key} from localStorage`, e)
        }
        return undefined
    }

    get<T>(key).then((stored) => {
        if (stored !== undefined) {
            data.value = options.mergeDefaults
                ? Object.assign({}, initialValue, stored)
                : stored
        } else {
            const migratedValue = tryMigrateFromLocalStorage()
            if (migratedValue !== undefined) {
                data.value = options.mergeDefaults
                    ? Object.assign({}, initialValue, migratedValue)
                    : migratedValue

                persist()
            }
        }
    }).catch((err) => {
        console.error(`Failed to read ${key} from IndexedDB`, err)
    })

    const persist = () => {
        const clean = cloneDeep(data.value)
        set(key, clean).catch((err) =>
            console.error(`Failed to write ${key} to IndexedDB`, err)
        )
    }

    watch(data, () => {
        persist()
    }, { deep: true })

    return data
}